import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { AdminLayout } from "./Dashboard";
import { useTheme } from "@/contexts/ThemeContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
    CreditCard, MessageSquare, Palette, Save, Loader2, Plus, Trash2,
    Sun, Moon, ShieldOff, ShieldCheck, RefreshCcw, AlertTriangle,
    UserCog, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";


const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });


const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-start gap-3 mb-4">
        <div className="rounded-md bg-electric/10 p-2">
            <Icon className="h-5 w-5 text-electric" />
        </div>
        <div>
            <h2 className="font-heading text-xl font-black text-white uppercase tracking-wider leading-tight">
                {title}
            </h2>
            {subtitle && <p className="text-white/60 text-sm mt-0.5">{subtitle}</p>}
        </div>
    </div>
);


const ToggleRow = ({ label, description, checked, onChange, testId }) => (
    <div className="flex items-start gap-4 p-3 rounded bg-obsidian border border-border">
        <div className="flex-1">
            <div className="text-white text-sm font-medium">{label}</div>
            {description && <div className="text-white/50 text-xs mt-0.5">{description}</div>}
        </div>
        <Switch
            checked={!!checked}
            onCheckedChange={onChange}
            data-testid={testId}
        />
    </div>
);


const AccessLevelsGrid = ({ catalogue, values, onChange }) => {
    const { keys, labels, roles, role_labels } = catalogue || {};
    if (!keys || !roles) return null;

    const toggle = (role, key) => {
        const roleMap = { ...(values?.[role] || {}) };
        roleMap[key] = !roleMap[key];
        onChange({ ...(values || {}), [role]: roleMap });
    };

    const setAll = (role, on) => {
        const roleMap = {};
        keys.forEach((k) => { roleMap[k] = on; });
        onChange({ ...(values || {}), [role]: roleMap });
    };

    return (
        <div className="overflow-x-auto" data-testid="access-levels-grid">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-white/40 font-heading border-b border-border">
                        <th className="text-left p-2 sticky left-0 bg-paper z-10">Permission</th>
                        <th className="text-center p-2 w-16 opacity-50" title="Full admin — always enabled">
                            <div className="flex items-center justify-center gap-1 text-emerald-400/80">
                                <ShieldCheck className="h-3 w-3" /> Admin
                            </div>
                        </th>
                        {roles.map((r) => (
                            <th key={r} className="text-center p-2 min-w-[120px]" data-testid={`role-col-${r}`}>
                                <div className="text-white text-[11px] font-bold uppercase tracking-wider">
                                    {role_labels?.[r] || r}
                                </div>
                                <div className="flex justify-center gap-1 mt-1">
                                    <button
                                        onClick={() => setAll(r, true)}
                                        className="text-[9px] text-emerald-400/70 hover:text-emerald-400 uppercase tracking-wider"
                                        data-testid={`role-allow-all-${r}`}
                                    >
                                        Allow all
                                    </button>
                                    <span className="text-white/20">|</span>
                                    <button
                                        onClick={() => setAll(r, false)}
                                        className="text-[9px] text-red-400/70 hover:text-red-400 uppercase tracking-wider"
                                        data-testid={`role-deny-all-${r}`}
                                    >
                                        Deny all
                                    </button>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {keys.map((k, i) => (
                        <tr key={k} className={i % 2 === 0 ? "bg-obsidian/40" : ""}>
                            <td className="p-2 text-white text-sm sticky left-0 bg-paper z-0" title={k}>
                                <div className="flex flex-col">
                                    <span>{labels?.[k] || k}</span>
                                    <span className="text-white/40 text-[10px] font-mono">{k}</span>
                                </div>
                            </td>
                            <td className="p-2 text-center">
                                <CheckCircle2 className="h-4 w-4 text-emerald-400/80 inline" />
                            </td>
                            {roles.map((r) => {
                                const on = !!values?.[r]?.[k];
                                return (
                                    <td key={r} className="p-2 text-center" data-testid={`perm-cell-${r}-${k}`}>
                                        <button
                                            onClick={() => toggle(r, k)}
                                            className={`h-6 w-6 rounded flex items-center justify-center transition-colors ${
                                                on
                                                    ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                                                    : "bg-red-500/10 text-red-400/60 hover:bg-red-500/20"
                                            }`}
                                            data-testid={`perm-toggle-${r}-${k}`}
                                            aria-label={`${on ? "Revoke" : "Grant"} ${labels?.[k]} for ${r}`}
                                        >
                                            {on ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                                        </button>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


const PlanEditor = ({ title, plans, onChange, testIdPrefix }) => {    const update = (idx, field, value) => {
        const copy = plans.map((p, i) => (i === idx ? { ...p, [field]: value } : p));
        onChange(copy);
    };
    const remove = (idx) => onChange(plans.filter((_, i) => i !== idx));
    const add = () => onChange([
        ...plans,
        {
            id: `plan-${Date.now()}`,
            name_sw: "Mpango mpya",
            name_en: "New plan",
            period: "day",
            price: 0,
            currency: "TZS",
        },
    ]);

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-editor`}>
            <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-wider text-white/60 font-heading">{title}</span>
                <Button size="sm" variant="outline" onClick={add} className="h-7 text-xs" data-testid={`${testIdPrefix}-add`}>
                    <Plus className="h-3 w-3 mr-1" /> Add plan
                </Button>
            </div>
            {(plans || []).length === 0 && (
                <div className="text-center py-4 text-white/40 text-xs">No plans yet. Add one above.</div>
            )}
            {(plans || []).map((p, i) => (
                <div key={p.id || i} className="grid grid-cols-12 gap-2 items-center p-2 rounded bg-obsidian border border-border" data-testid={`${testIdPrefix}-row-${i}`}>
                    <Input
                        className="col-span-3 bg-paper border-border text-white text-xs h-8"
                        value={p.name_en || ""}
                        onChange={(e) => update(i, "name_en", e.target.value)}
                        placeholder="Name (EN)"
                    />
                    <Input
                        className="col-span-3 bg-paper border-border text-white text-xs h-8"
                        value={p.name_sw || ""}
                        onChange={(e) => update(i, "name_sw", e.target.value)}
                        placeholder="Jina (SW)"
                    />
                    <select
                        className="col-span-2 bg-paper border border-border text-white text-xs rounded h-8 px-2"
                        value={p.period || "day"}
                        onChange={(e) => update(i, "period", e.target.value)}
                    >
                        <option value="day">Day</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                    </select>
                    <Input
                        type="number"
                        min="0"
                        className="col-span-2 bg-paper border-border text-white text-xs h-8 tabular-nums"
                        value={p.price ?? 0}
                        onChange={(e) => update(i, "price", parseInt(e.target.value || "0", 10))}
                        placeholder="Price"
                    />
                    <Input
                        className="col-span-1 bg-paper border-border text-white text-xs h-8"
                        value={p.currency || "TZS"}
                        onChange={(e) => update(i, "currency", e.target.value.toUpperCase())}
                        placeholder="TZS"
                    />
                    <button
                        onClick={() => remove(i)}
                        className="col-span-1 text-white/40 hover:text-red-400 flex justify-center"
                        data-testid={`${testIdPrefix}-remove-${i}`}
                        aria-label="Remove plan"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            ))}
        </div>
    );
};


const PlatformSettings = () => {
    const { token } = useAuth();
    const { refresh: refreshTheme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [settings, setSettings] = useState(null);
    const [catalogue, setCatalogue] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [settingsRes, catalogueRes] = await Promise.all([
                axios.get(`${API}/settings/platform`, authHeaders(token)),
                axios.get(`${API}/settings/permissions/catalogue`, authHeaders(token)),
            ]);
            setSettings(settingsRes.data);
            setCatalogue(catalogueRes.data);
            setDirty(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load settings");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const patch = (section, key, value) => {
        setSettings((prev) => ({
            ...prev,
            [section]: { ...(prev?.[section] || {}), [key]: value },
        }));
        setDirty(true);
    };

    const patchSection = (section, value) => {
        setSettings((prev) => ({ ...prev, [section]: value }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                billing: settings.billing,
                sms: settings.sms,
                theme: settings.theme,
                access_levels: settings.access_levels,
            };
            const r = await axios.put(`${API}/settings/platform`, body, authHeaders(token));
            setSettings(r.data);
            setDirty(false);
            toast.success("Settings saved");
            refreshTheme();  // apply new dark/light state immediately
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const resetDefaults = async () => {
        if (!window.confirm("Reset all platform settings to factory defaults?")) return;
        setSaving(true);
        try {
            const r = await axios.post(`${API}/settings/platform/reset`, {}, authHeaders(token));
            setSettings(r.data);
            setDirty(false);
            toast.success("Settings reset to defaults");
            refreshTheme();
        } catch (e) {
            toast.error("Reset failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !settings) {
        return (
            <AdminLayout title="Control & Management">
                <div className="flex items-center justify-center py-24 text-white/50" data-testid="platform-settings-loading">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading settings...
                </div>
            </AdminLayout>
        );
    }

    const billing = settings.billing || {};
    const sms = settings.sms || {};
    const theme = settings.theme || {};

    return (
        <AdminLayout title="Control & Management">
            <div className="max-w-5xl mx-auto space-y-6 pb-24" data-testid="platform-settings-page">
                {/* Header + action buttons */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h1 className="font-heading text-3xl font-black text-white uppercase tracking-wider">
                            Control & Management
                        </h1>
                        <p className="text-white/60 text-sm mt-0.5">
                            Billing plans, SMS quotas, and theme toggles for the whole platform.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={resetDefaults}
                            disabled={saving}
                            data-testid="settings-reset-btn"
                            className="border-border text-white/70"
                        >
                            <RefreshCcw className="h-4 w-4 mr-1" /> Reset defaults
                        </Button>
                        <Button
                            onClick={save}
                            disabled={!dirty || saving}
                            className="bg-electric hover:bg-electric/80 text-white font-bold"
                            data-testid="settings-save-btn"
                        >
                            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : <><Save className="h-4 w-4 mr-1" /> Save changes</>}
                        </Button>
                    </div>
                </div>

                {dirty && (
                    <Card className="bg-yellow-500/10 border-yellow-500/40 p-3 flex items-center gap-3" data-testid="settings-dirty-banner">
                        <AlertTriangle className="h-4 w-4 text-yellow-300" />
                        <span className="text-yellow-200 text-sm">You have unsaved changes.</span>
                    </Card>
                )}

                {/* Billing */}
                <Card className="bg-paper border-border p-6" data-testid="settings-billing-section">
                    <SectionHeader
                        icon={CreditCard}
                        title="Billing"
                        subtitle="Master switches, subscription plans and free-interaction grace."
                    />
                    <div className="grid gap-3 md:grid-cols-2 mb-5">
                        <ToggleRow
                            label="Web billing enabled"
                            description="When off, paywalls on web are bypassed — everything is free to view."
                            checked={billing.web_billing_enabled}
                            onChange={(v) => patch("billing", "web_billing_enabled", v)}
                            testId="toggle-web-billing"
                        />
                        <ToggleRow
                            label="SMS billing enabled"
                            description="When off, SMS chat has no paywall — quota rate-limit still applies."
                            checked={billing.sms_billing_enabled}
                            onChange={(v) => patch("billing", "sms_billing_enabled", v)}
                            testId="toggle-sms-billing"
                        />
                    </div>

                    <div className="p-3 rounded bg-obsidian border border-border mb-5">
                        <Label className="text-[11px] uppercase tracking-wider text-white/60 font-heading">
                            Free SMS interactions before paywall
                        </Label>
                        <div className="flex items-center gap-3 mt-1">
                            <Input
                                type="number"
                                min="0"
                                max="100"
                                value={billing.free_sms_interactions ?? 3}
                                onChange={(e) => patch("billing", "free_sms_interactions", parseInt(e.target.value || "0", 10))}
                                className="bg-paper border-border text-white w-24 tabular-nums"
                                data-testid="input-free-sms"
                            />
                            <span className="text-white/50 text-xs">
                                Customer gets this many billable replies before Dimbani asks them to pay.
                            </span>
                        </div>
                    </div>

                    <div className="space-y-5">
                        <PlanEditor
                            title="Web prediction plans"
                            plans={billing.web_plans || []}
                            onChange={(v) => patch("billing", "web_plans", v)}
                            testIdPrefix="web-plans"
                        />
                        <PlanEditor
                            title="SMS chat / content plans"
                            plans={billing.sms_plans || []}
                            onChange={(v) => patch("billing", "sms_plans", v)}
                            testIdPrefix="sms-plans"
                        />
                    </div>
                </Card>

                {/* SMS quotas */}
                <Card className="bg-paper border-border p-6" data-testid="settings-sms-section">
                    <SectionHeader
                        icon={MessageSquare}
                        title="SMS quotas"
                        subtitle="How many messages the platform can send + how many each customer can chat."
                    />
                    <ToggleRow
                        label="Auto-SMS engine"
                        description="Master switch for real-time event alerts (goals, red cards, etc.)."
                        checked={sms.auto_sms_enabled}
                        onChange={(v) => patch("sms", "auto_sms_enabled", v)}
                        testId="toggle-auto-sms"
                    />
                    <div className="grid gap-3 md:grid-cols-2 mt-4">
                        <div className="p-3 rounded bg-obsidian border border-border">
                            <Label className="text-[11px] uppercase tracking-wider text-white/60 font-heading">
                                Daily outbound quota (auto-SMS)
                            </Label>
                            <Input
                                type="number"
                                min="0"
                                value={sms.daily_outbound_quota ?? 500}
                                onChange={(e) => patch("sms", "daily_outbound_quota", parseInt(e.target.value || "0", 10))}
                                className="bg-paper border-border text-white mt-1 tabular-nums"
                                data-testid="input-daily-outbound"
                            />
                            <p className="text-white/50 text-[11px] mt-1">
                                Once reached, the auto-SMS engine pauses until midnight UTC. 0 = unlimited.
                            </p>
                        </div>
                        <div className="p-3 rounded bg-obsidian border border-border">
                            <Label className="text-[11px] uppercase tracking-wider text-white/60 font-heading">
                                Daily chat limit per phone
                            </Label>
                            <Input
                                type="number"
                                min="0"
                                value={sms.daily_chat_limit_per_phone ?? 20}
                                onChange={(e) => patch("sms", "daily_chat_limit_per_phone", parseInt(e.target.value || "0", 10))}
                                className="bg-paper border-border text-white mt-1 tabular-nums"
                                data-testid="input-daily-chat-limit"
                            />
                            <p className="text-white/50 text-[11px] mt-1">
                                Max inbound messages each phone can send per day. 0 = unlimited.
                            </p>
                        </div>
                    </div>
                </Card>

                {/* Theme */}
                <Card className="bg-paper border-border p-6" data-testid="settings-theme-section">
                    <SectionHeader
                        icon={Palette}
                        title="Theme"
                        subtitle="Applies globally in real-time — no rebuild required."
                    />
                    <div className="grid gap-3 md:grid-cols-2">
                        <ToggleRow
                            label={<span className="flex items-center gap-2">{theme.admin_dark_mode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />} Admin dark mode</span>}
                            description={theme.admin_dark_mode ? "Admin dashboard uses the default dark palette." : "Admin dashboard uses a light palette."}
                            checked={theme.admin_dark_mode}
                            onChange={(v) => patch("theme", "admin_dark_mode", v)}
                            testId="toggle-admin-dark"
                        />
                        <ToggleRow
                            label={<span className="flex items-center gap-2">{theme.public_dark_mode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />} Public site dark mode</span>}
                            description={theme.public_dark_mode ? "Public Swahili site uses the default dark palette." : "Public Swahili site uses a light palette."}
                            checked={theme.public_dark_mode}
                            onChange={(v) => patch("theme", "public_dark_mode", v)}
                            testId="toggle-public-dark"
                        />
                    </div>
                </Card>

                {/* Access Levels — role → permission matrix */}
                <Card className="bg-paper border-border p-6" data-testid="settings-access-section">
                    <SectionHeader
                        icon={UserCog}
                        title="Access levels"
                        subtitle="Fine-grained permissions per role. Admins always have every permission."
                    />
                    <AccessLevelsGrid
                        catalogue={catalogue}
                        values={settings.access_levels || {}}
                        onChange={(next) => { patchSection("access_levels", next); }}
                    />
                </Card>

                {/* Status */}
                <Card className="bg-obsidian border-border p-4 flex items-center gap-2" data-testid="settings-status">
                    {billing.web_billing_enabled && billing.sms_billing_enabled && sms.auto_sms_enabled ? (
                        <><ShieldCheck className="h-4 w-4 text-emerald-400" /> <span className="text-emerald-300 text-sm">All revenue rails active.</span></>
                    ) : (
                        <><ShieldOff className="h-4 w-4 text-yellow-300" /> <span className="text-yellow-200 text-sm">Some rails are switched off — customers may see free/limited experiences.</span></>
                    )}
                    {settings.updated_at && (
                        <span className="ml-auto text-white/40 text-[11px]">
                            Last updated: {new Date(settings.updated_at).toLocaleString()}
                        </span>
                    )}
                </Card>
            </div>
        </AdminLayout>
    );
};


export default PlatformSettings;
